import * as Yup from "yup";

const sqlInjectionRegex = /('|"|;|\b(--|\|\||\/\*|\*\/|EXEC(\s|\+)+(SP|XP)|DECLARE\s+[@#]|SELECT\s+.*\s+FROM|INSERT\s+INTO|DELETE\s+FROM|DROP\s+TABLE|UPDATE\s+.*\s+SET|UNION\s+SELECT))\b/i;
const emailRules = /^(?!\.)(?!.*\.\.)[A-Za-z0-9._%+-]+@(?!(?:[-]))[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const zipCodeRegExp = /^[0-9]{4,10}$/; // Only numeric zipcodes between 4 to 10 digits

const noSQLInjection = (fieldName) =>
    Yup.string()
        .test(
            'no-sql-injection',
            `${fieldName} contains potentially dangerous characters`,
            value => !value || !sqlInjectionRegex.test(value)
        );

const selectField = Yup.string().transform((value, originalValue) => {
    if (typeof originalValue === 'object' && originalValue !== null && 'value' in originalValue) {
        return String(originalValue.value); // ensures string type
    }
    return originalValue;
});


export const bookingValidationSchema = Yup.object({
    origin_city: Yup.mixed().notRequired(),
    destination_city: Yup.mixed().notRequired(),
    origin_port: Yup.mixed().notRequired(),
    destination_port: Yup.mixed().notRequired(),
    cargo_type: Yup.mixed().notRequired(),
    commodity: Yup.mixed().notRequired(),
    cargo_value: Yup.number().typeError('Cargo value must be a number').required('Cargo value is required'),
    incoterms: Yup.mixed().notRequired(),
    container_type: Yup.array().of(
        Yup.object().shape({
            quantity: Yup.number().typeError('Quantity must be a number').required('Quantity is required'),
            type: Yup.string().required('Type is required'),
        })
    ),
    gross_weight: Yup.string().required('Gross weight is required'),
    shipment_details: Yup.mixed().notRequired(),
    isBillingAddress: Yup.boolean(),
    
    firstName: Yup.string().required('Shipper First name is required'),
    lastName: Yup.mixed().notRequired(),
    companyName: Yup.mixed().notRequired(),
    contactNumber: Yup.string().required("Shipper Contact Number is required").matches(/^\d{10}$/, "Please enter valid phone number").concat(noSQLInjection('Shipper phone number is required')),
    email: Yup.string()
            .notRequired()
            .max(100, 'Email must be 100 characters or less')
            .matches(/^\S+$/, 'Email must not contain spaces')
            .matches(/^[\x00-\x7F]+$/, 'Email must be ASCII only')
            .matches(/^[^<>]*$/, 'Invalid characters detected')
            .matches(emailRules, 'Please enter a valid email address')
            .concat(noSQLInjection('Shipper Email is required')),
    companyAddress: Yup.mixed().notRequired(),
    shipperCityId: Yup.mixed().notRequired(),
    shipperCity: Yup.string(),
    shipperStateId: Yup.string(),
    shipperState: Yup.string(),
    shipperCountryId: Yup.string(),
    shipperCountry: Yup.string(),
    shipperZipcode: Yup
        .string()
        .trim()
        .notRequired()
        .when("zipcode", (val, schema) =>
          val
            ? schema
              .required("Zipcode is required")
              .matches(zipCodeRegExp, "Zipcode must be numeric and 4–10 digits")
            : schema
        ).concat(noSQLInjection("Zipcode")),

    consigneeFirstName: Yup.string()
            .max(100, 'Consignee Last Name cannot exceed 100 characters')
            .required("Consignee first name is required")
            .concat(noSQLInjection('Consignee last name is required')),

    consigneeLastName: Yup.string()
            .max(100, 'Consignee Last Name cannot exceed 100 characters')
            .notRequired()
            .concat(noSQLInjection('Consignee last name is required')),
    consigneePhoneNumber: Yup.string().required("Consignee phone number is required").matches(/^\d{10}$/, "Please enter valid phone number").concat(noSQLInjection('Consignee phone number is required')),
    consigneeOpCode: Yup.mixed().notRequired(),
    consigneeEmail: Yup.string()
            .notRequired()
            .max(100, 'Email must be 100 characters or less')
            .matches(/^\S+$/, 'Email must not contain spaces')
            .matches(/^[\x00-\x7F]+$/, 'Email must be ASCII only')
            .matches(/^[^<>]*$/, 'Invalid characters detected')
            .matches(emailRules, 'Please enter a valid email address')
            .concat(noSQLInjection('Consignee Email is required')),
    consigneeCompanyName: Yup.string()
            .max(255, 'Consignee Company name cannot exceed 255 characters')
            .notRequired()
            .concat(noSQLInjection('Consignee Company Name is InValid')),
    consigneeCompanyAddress: Yup.string()
            .max(255, 'Consignee Company Address cannot exceed 255 characters')
            .notRequired()
            .concat(noSQLInjection('Consignee Company Address is InValid')),
    consigneeCityId: Yup.string().required('Consignee City is required'),
    consigneeCity: Yup.string().required('Consignee City is required'),
    consigneeStateId: Yup.string().notRequired(),
    consigneeState: Yup.string().notRequired(),
    consigneeCountryId: Yup.string().notRequired(),
    consigneeCountry: Yup.string().notRequired(),
    consigneeZipcode: Yup
        .string()
        .trim()
        .notRequired()
        .when("zipcode", (val, schema) =>
          val
            ? schema
              .required("Zipcode is required")
              .matches(zipCodeRegExp, "Zipcode must be numeric and 4–10 digits")
            : schema
        ).concat(noSQLInjection("Zipcode")),

    billingCustomerName: selectField.required('Billing Customer is required'),
    billingCustomerAddress: Yup.string()
            .max(255, 'Customer Address cannot exceed 255 characters')
            .notRequired()
            .concat(noSQLInjection('Customer Address')),
    billingContactName: Yup.string().max(100, 'Contact Name cannot exceed 100 characters')
            .notRequired()
            .concat(noSQLInjection('Contact Name')),
    billingContactEmail: Yup.string()
            .notRequired()
            .max(100, 'Email must be 100 characters or less')
            .matches(/^\S+$/, 'Email must not contain spaces')
            .matches(/^[\x00-\x7F]+$/, 'Email must be ASCII only')
            .matches(/^[^<>]*$/, 'Invalid characters detected')
            .matches(emailRules, 'Please enter a valid email address')
            .concat(noSQLInjection('Contact Email')),
    billingContactNumber: Yup.string().notRequired().matches(/^\d{10}$/, "Please enter valid phone number").concat(noSQLInjection('Billing Contact Number')),
});